/**
 * Date-range helpers. The analytics endpoints take from_date/to_date as
 * inclusive YYYY-MM-DD strings in local terms, so everything here works in
 * local time rather than UTC — using toISOString() would shift the day for
 * anyone west of Greenwich, which includes Jamaica.
 */

export interface DateRange {
  from: string
  to: string
}

export function isoDate(date: Date): string {
  const year = date.getFullYear()
  const month = String(date.getMonth() + 1).padStart(2, '0')
  const day = String(date.getDate()).padStart(2, '0')
  return `${year}-${month}-${day}`
}

function daysAgo(days: number): Date {
  const date = new Date()
  date.setDate(date.getDate() - days)
  return date
}

export interface RangePreset {
  key: string
  label: string
  range: () => DateRange
}

export const PRESETS: RangePreset[] = [
  {
    key: 'today',
    label: 'Today',
    range: () => ({ from: isoDate(new Date()), to: isoDate(new Date()) }),
  },
  {
    key: 'last7',
    label: 'Last 7 days',
    range: () => ({ from: isoDate(daysAgo(6)), to: isoDate(new Date()) }),
  },
  {
    key: 'last30',
    label: 'Last 30 days',
    range: () => ({ from: isoDate(daysAgo(29)), to: isoDate(new Date()) }),
  },
  {
    key: 'mtd',
    label: 'This month',
    range: () => {
      const now = new Date()
      return { from: isoDate(new Date(now.getFullYear(), now.getMonth(), 1)), to: isoDate(now) }
    },
  },
  {
    key: 'lastMonth',
    label: 'Last month',
    range: () => {
      const now = new Date()
      const first = new Date(now.getFullYear(), now.getMonth() - 1, 1)
      const last = new Date(now.getFullYear(), now.getMonth(), 0)
      return { from: isoDate(first), to: isoDate(last) }
    },
  },
  {
    key: 'ytd',
    label: 'This year',
    range: () => {
      const now = new Date()
      return { from: isoDate(new Date(now.getFullYear(), 0, 1)), to: isoDate(now) }
    },
  },
]

export function defaultRange(): DateRange {
  return PRESETS[2]!.range() // Last 30 days
}

/** Which preset, if any, a range corresponds to — for highlighting the chips. */
export function matchPreset(range: DateRange): string | null {
  for (const preset of PRESETS) {
    const candidate = preset.range()
    if (candidate.from === range.from && candidate.to === range.to) return preset.key
  }
  return null
}

/**
 * Every bucket start in an inclusive range, as the API's trend series key
 * them (Postgres date_trunc): each day, the Monday of each week, or the 1st
 * of each month. The series only lists buckets that had sales, so charts use
 * this to put the empty ones back — a product that sold once then sits on its
 * day instead of one bar filling the chart.
 *
 * Pure calendar arithmetic, done in UTC so no offset can shift a day; the
 * API's buckets are UTC days too.
 */
export function bucketStarts(from: string, to: string, bucket: string): string[] {
  const parse = (value: string) => {
    const [year, month, day] = value.split('-').map(Number)
    return new Date(Date.UTC(year ?? NaN, (month ?? NaN) - 1, day ?? NaN))
  }
  const date = parse(from)
  const end = parse(to)
  if (Number.isNaN(date.getTime()) || Number.isNaN(end.getTime())) return []
  if (bucket === 'week') date.setUTCDate(date.getUTCDate() - ((date.getUTCDay() + 6) % 7))
  if (bucket === 'month') date.setUTCDate(1)

  const starts: string[] = []
  // Ten years of days at most, in case of a nonsense range.
  while (date <= end && starts.length < 3700) {
    starts.push(date.toISOString().slice(0, 10))
    if (bucket === 'month') date.setUTCMonth(date.getUTCMonth() + 1)
    else date.setUTCDate(date.getUTCDate() + (bucket === 'week' ? 7 : 1))
  }
  return starts
}
